import React from "react"
import { useInView } from "react-intersection-observer"

import { CONSTANTS } from "@data"
import { css } from "@jsUtils"
import { joinNewLines } from "@jsUtils/contentful"

import { CTA, Eyebrow, Headline } from "@shared"

import Styles from "./pageheading.module.scss"

const { COLORS } = CONSTANTS

/**
 * Render a generic page heading
 * @param {object} props - react props
 * @param {object} props.data - content
 * @param {string} props.inlineText - inline header content
 * @param {string} props.inlineClass - inline header content class
 * @param {string} props.extraClass - extra class to add to the wrapper
 * @param {string} props.categroy - extra category tag to display
 * @param {string} props.copy - extra text content to display
 * @param {string} props.copyCtaLink - inline cta link
 * @param {string} props.copyCtaText - inline cta text
 * @param {string} props.color - color property
 * @param {boolean} props.joinHeadline - do we want to parse/join the headline data?
 */
const PageHeading = ({
  data,
  joinHeadline,
  ready,
  onReady,
  inlineText,
  inlineClass,
  extraClass,
  category,
  copy,
  copyCtaLink,
  copyCtaText,
  color = COLORS.FLURO
}) => {
  const txt = joinHeadline ? joinNewLines(data) : data

  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  })
  React.useEffect(() => {
    if (inView && !ready && onReady) {
      onReady()
    }
  }, [inView])

  return (
    <div className={css(Styles.pageHeading, ready && Styles.ready, extraClass)} ref={ref}>
      {category && <Eyebrow color={color}>{category}</Eyebrow>}
      {data && (
        <Headline size={1} className={Styles.headline}>
          {txt && (
            // eslint-disable-next-line react/no-danger
            <span dangerouslySetInnerHTML={{ __html: txt }} />
          )}
          {inlineText && (
            <>
              <span className={Styles.wedge}>-</span>
              <span className={css(Styles.inline, inlineClass)}>{inlineText}</span>
            </>
          )}
        </Headline>
      )}
      {copy && <p className={Styles.description}>{copy}</p>}
      {copyCtaText && copyCtaLink && (
        <CTA to={copyCtaLink} color={color} ready className={css(Styles.cta, Styles.ready)}>
          {copyCtaText}
        </CTA>
      )}
    </div>
  )
}

export default PageHeading
